<template>
    <div class="background">
        <div id="sca-payment-request-button" class="payment-request">
        </div>
        <PaymentError :error="displayError"/>
        <FacebookBrowserMessage style="width: 100%" v-if="showFacebookMessage" :subdomain="subdomain"/>
    </div>
</template>

<script>
import PaymentError from '../PaymentError.vue';
import {
  instantiateStripe,
  pay,
  confirmSecretWithNoPaymentMethod,
  confirmPayment,
  mountPaymentRequestsButton,
  createPaymentRequest,
  confirmSecretDeferred,
} from '../stripe-elements';
import PaymentsMixin from '../PaymentsMixin.vue';
import FacebookBrowserMessage from '../FacebookBrowserMessage.vue';

export default {
  name: 'SCAPaymentRequestButton',
  mixins: [PaymentsMixin],
  data() {
    return {
      canUsePaymentRequest: false,
      displayError: '',
    };
  },
  props: {
    stripePublicKey: {
      type: String,
      required: true,
    },
    stripeAccountId: {
      type: String,
      required: true,
    },
    orderId: {
      type: String,
      required: false,
    },
    paymentRequestOptions: {
      required: true,
      type: Object,
    },
    subdomain: {
      required: true,
      type: String,
    },
  },
  methods: {
    async submitPaymentRequest(event) {
      this.$emit('emit-global-loading', true);
      this.displayError = '';
      this.submitPayment(event);
    },

    async submitPayment(event) {
      const { data: intent } = await pay(this.orderId);
      if (intent.type === 'error') {
        this.handleErrorMessages(intent);
      } else if (this.confirmPaymentHasSucceeded(intent)) {
        this.handleSuccessfulPayment(intent);
      } else {
        const clientSecret = intent.client_secret;
        const { paymentIntent, error: confirmError } = await confirmSecretDeferred(clientSecret, event.paymentMethod.id);
        if (confirmError) {
          this.handleErrorMessages(confirmError);
          event.complete('fail');
        } else {
          await event.complete('success');
          // does the payment require sca
          if (paymentIntent.status === 'requires_action' || paymentIntent.status === 'requires_source_action') {
            // sca
            const { error } = await confirmSecretWithNoPaymentMethod(clientSecret);
            if (error) {
              this.handleErrorMessages(error);
            } else {
              this.handleSuccessfulPayment(paymentIntent);
            }
          } else {
            // non sca
            this.handleSuccessfulPayment(paymentIntent);
          }
        }
      }
    },

    async handleSuccessfulPayment(paymentIntent) {
      const result = await confirmPayment(paymentIntent.id, this.orderId);
      if (result.order !== undefined) {
        const {uuid} = result.order;
        const url = `/order/${uuid}`;

        this.$emit('payment-successful', uuid);

        window.location.href = url;
      } else {
        this.handleErrorMessages(result.data);
      }
    },
  },

  beforeMount() {
    const { stripeAccountId, stripePublicKey } = this;
    instantiateStripe(Stripe, {
      stripeAccountId,
      stripePublicKey,
    });
  },

  async mounted() {
    const paymentRequest = createPaymentRequest(this.paymentRequestOptions);
    const prButton = mountPaymentRequestsButton(paymentRequest);
    const nodeId = '#sca-payment-request-button';
    paymentRequest.canMakePayment()
      .then((result) => {
        const button = document.querySelector(nodeId);
        if (result) {
          this.$emit('valid', true);
          if (!result.applePay) {
            button.classList.add('google-pay');
            button.disabled = true;
            button.addEventListener('click', () => {
              this.displayError = '';
              if (!this.loading) {
                paymentRequest.show();
              }
            });
          } else {
            prButton.mount(nodeId);
          }
        } else {
          this.paymentRequestFailedToMount = true;
          button.style.display = 'none';
        }
      });

    paymentRequest.on('paymentmethod', this.submitPaymentRequest);
  },

  components: {
    PaymentError,
    FacebookBrowserMessage,
  },
};
</script>

<style scoped>

    .background {
        padding: 0 16px;
    }

    #sca-payment-request-button {
        padding-bottom: 16px;
    }

    /*
      Some of the seemingly arbitrary figures come from this doc
      https://developers.google.com/pay/api/android/guides/brand-guidelines#:~:text=The%20Google%20Pay%20button%20should,minimum%20width%20of%20152%20dp.
    */
    #sca-payment-request-button.google-pay {
        background-color: #444;
        background: url(/assets-prod/images/svg-sprites/Google-Pay-Logo.svg) no-repeat center #fff;
        margin: 0 auto;
        height: 40px;
        background-size: 48px;
        min-width: 160px;
        /*max-width: 24vw;*/
        cursor: pointer;
        text-align: center;
        padding: 8px;
        border: 1px solid #fafafa;
        border-radius: 4px;
        will-change: box-shadow;
        box-shadow: 0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12);
    }

    .google-pay:after {
        content: '\0020'
    }


</style>
